<template>
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-ink/50 px-4"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-sm max-h-[85dvh] overflow-y-auto paper-card rounded-md p-5">
      <div class="flex items-start justify-between gap-2 mb-1">
        <h2 :id="dialogTitleId" class="text-[16px] font-semibold">{{ $t('gradeModal.title') }}</h2>
        <button
          @click="$emit('close')"
          class="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('gradeModal.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
      <p class="text-[12px] text-ink2 mb-4">{{ $t('gradeModal.subtitle') }}</p>

      <div class="space-y-2">
        <div
          v-for="g in GRADES"
          :key="g.grade"
          class="rounded-md border p-3 transition-colors"
          :class="g.grade === currentGrade
            ? 'border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.08)]'
            : 'border-[hsl(var(--border)/0.6)]'"
        >
          <div class="flex items-center gap-2 mb-1">
            <span
              class="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
              :style="{ background: g.color }"
            >
              <Mountain class="w-3.5 h-3.5 text-white" />
            </span>
            <span class="text-[13px] font-semibold">T{{ g.grade }} — {{ g.label }}</span>
            <span
              v-if="g.grade === currentGrade"
              class="ml-auto shrink-0 text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white"
            >{{ $t('gradeModal.thisRoute') }}</span>
          </div>
          <p class="text-[12px] text-[hsl(var(--foreground)/0.75)] leading-snug">{{ g.description }}</p>
        </div>
      </div>

      <p class="text-[11px] text-ink2 mt-4 pt-3 border-t border-[hsl(var(--border)/0.6)]">
        {{ $t('gradeModal.footer') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Mountain, X } from "lucide-vue-next";

defineProps<{ currentGrade?: number | null }>();
const emit = defineEmits<{ (e: "close"): void }>();

const { t } = useI18n();

const GRADE_COLORS: Record<number, string> = {
  1: "hsl(155,55%,42%)",
  2: "hsl(155,55%,42%)",
  3: "hsl(38,92%,50%)",
  4: "hsl(25,90%,52%)",
  5: "hsl(0,72%,51%)",
  6: "hsl(0,65%,38%)",
};

const GRADES = computed(() =>
  [1, 2, 3, 4, 5, 6].map((grade) => ({
    grade,
    label: t(`routeDetails.grade.${grade}`),
    color: GRADE_COLORS[grade],
    description: t(`gradeModal.descriptions.${grade}`),
  }))
);

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('close') });
</script>
